import React, { useEffect, useMemo } from "react";
import CustomReactTable from "../../../components/ReactTable/CustomReactTable";
import { useDispatch, useSelector } from "react-redux";
import { Controller, useForm } from "react-hook-form";
import {
  editBrandType,
  getAllBrandTypes,
  getVehicleModels,
  patchEditBrandTypeModal,
} from "../../../redux/CarsManagement/BrandTypes/brandTypesSlice";
import StatusSelect from "../../../components/CustomSelect/StatusSelect";
import MaintenanceSelect from "../../../components/CustomSelect/MaintenanceSelect";
import CustomInput from "../../../components/CustomInput/CustomInput";

function BrandTypesGrid({ brandId }) {
  const dispatch = useDispatch();

  const {
    allBrandTypes,
    editBrandTypesModalValues,
    vehicleModels,
    isLoading,
    addOrEditResult,
  } = useSelector((state) => state.brandTypes);

  const {
    formState: { errors, isValid, isDirty },
    handleSubmit,
    reset,
    control,
    setValue,
  } = useForm({
    defaultValues: editBrandTypesModalValues,
  });

  // ✅ sync redux → form
  useEffect(() => {
    reset(editBrandTypesModalValues);
  }, [editBrandTypesModalValues, reset]);

  const resetEditBrandTypeModalValues = () => {
    reset(editBrandTypesModalValues);
  };

  // ✅ open edit modal
  const handleBrandTypeModalValues = (row) => {
    dispatch(patchEditBrandTypeModal(row));

    // optional: change this if you have brandName/brand_id mapping
    if (row?.brandName) {
      dispatch(getVehicleModels(row.brandName));
    }
  };

  // ✅ autofill from model
  const handleModelChange = (value) => {
    const selected = vehicleModels.find(
      (m) => m.modelDescription1 === value
    );

    if (selected) {
      setValue("nameEn", selected.modelDescription1);
      setValue("nameAr", selected.modelDescription2);
    }
  };

  // ✅ edit submit (IMPORTANT FIX: add id)
  const handleEditBrandType = (data) => {
    dispatch(
      editBrandType({
        id: editBrandTypesModalValues.id,
        ...data,
      })
    );
  };

  // ✅ load table data
  useEffect(() => {
    dispatch(getAllBrandTypes(brandId));
  }, [addOrEditResult, brandId, dispatch]);

  const columns = useMemo(
    () => [
      {
        Header: "الإسم",
        accessor: "nameAr",
      },
      {
        Header: "الحالة",
        accessor: "status",
        Cell: ({ row }) =>
          row.original.status ? (
            <span className="uk-text-success" uk-icon="check"></span>
          ) : (
            <span className="uk-text-danger" uk-icon="close"></span>
          ),
      },
      {
        Header: "الترتيب",
        accessor: "order",
      },
      {
        Header: "الإجراء",
        disableSortBy: true,
        Cell: ({ row }) => (
          <div className="actions uk-flex uk-flex-center uk-flex-middle">
            <button
              uk-tooltip="تعديل"
              className="uk-icon-button"
              uk-icon="pencil"
              uk-toggle="target: #editBrandTypeModal"
              onClick={() => handleBrandTypeModalValues(row.original)}
            ></button>
          </div>
        ),
      },
    ],
    []
  );

  return (
    <>
      {/* EDIT MODAL */}
      <div id="ModalOfEditBrandType">
        <div
          id="editBrandTypeModal"
          uk-modal="bg-close: false"
          container="#ModalOfEditBrand"
        >
          <div className="uk-modal-dialog">
            <button
              className="uk-modal-close-default"
              type="button"
              uk-close=""
              onClick={resetEditBrandTypeModalValues}
            />

            <div className="uk-modal-header uk-text-center">
              <h2 className="uk-modal-title">تعديل</h2>
            </div>

            <div className="uk-modal-body">
              <form className="uk-grid-small uk-child-width-1-2" uk-grid="">

                {/* MODEL DROPDOWN */}
                <div>
                  <label className="uk-form-label uk-text-gray uk-text-bold">
                    الموديل
                  </label>

                  <div className="uk-form-controls uk-margin-small-top">
                    <Controller
                      name="model"
                      control={control}
                      render={({ field }) => (
                        <select
                          className="uk-select"
                          {...field}
                          onChange={(e) => {
                            field.onChange(e);
                            handleModelChange(e.target.value);
                          }}
                        >
                          <option value="">اختر الموديل</option>

                          {vehicleModels.map((item, i) => (
                            <option
                              key={i}
                              value={item.modelDescription1}
                            >
                              {item.modelDescription1}
                            </option>
                          ))}
                        </select>
                      )}
                    />
                  </div>
                </div>

                {/* Arabic */}
                <div>
                  <label className="uk-form-label uk-text-gray uk-text-bold">
                    الإسم بالعربي
                  </label>
                  <CustomInput
                    control={control}
                    name="nameAr"
                    error={errors.nameAr}
                  />
                </div>

                {/* English */}
                <div>
                  <label className="uk-form-label uk-text-gray uk-text-bold">
                    الإسم بالإنجليزي
                  </label>
                  <CustomInput
                    control={control}
                    name="nameEn"
                    error={errors.nameEn}
                  />
                </div>

                {/* Order */}
                <div>
                  <label className="uk-form-label uk-text-gray uk-text-bold">
                    الترتيب
                  </label>
                  <CustomInput
                    control={control}
                    name="order"
                    type="number"
                    error={errors.order}
                  />
                </div>

                {/* Status */}
                <div>
                  <label className="uk-form-label uk-text-gray uk-text-bold">
                    الحالة
                  </label>

                  <Controller
                    name="status"
                    control={control}
                    render={({ field }) => (
                      <StatusSelect field={field} error={errors.status} />
                    )}
                  />
                </div>

                {/* Maintenance */}
                <div>
                  <label className="uk-form-label uk-text-gray uk-text-bold">
                    عرض في الصيانة
                  </label>

                  <Controller
                    name="viewinmaintenance"
                    control={control}
                    render={({ field }) => (
                      <MaintenanceSelect
                        field={field}
                        error={errors.viewinmaintenance}
                      />
                    )}
                  />
                </div>

              </form>
            </div>

            <div className="uk-modal-footer uk-text-left">
              <button
                className={`uk-button uk-button-secondary uk-margin-small-left uk-border-rounded${
                  isValid ? " uk-modal-close" : ""
                }`}
                type="button"
                disabled={!isDirty}
                onClick={handleSubmit(handleEditBrandType)}
              >
                تعديل
              </button>

              <button
                className="uk-button uk-button-primary uk-modal-close uk-border-rounded"
                type="button"
                onClick={resetEditBrandTypeModalValues}
              >
                إلغاء
              </button>
            </div>

          </div>
        </div>
      </div>

      {/* TABLE */}
      <CustomReactTable
        data={allBrandTypes}
        columns={columns}
        isLoading={isLoading}
      />
    </>
  );
}

export default BrandTypesGrid;